import classNames from "classnames";
import { CoButton } from "components/CoButton";
import SearchModeSelector from "components/SearchModeSelector";
import { FilterButton } from "features/FilterDrawer";
import useCheckSearchCredits from "features/SearchInput/hooks/useCheckSearchCredits";
import { useCanCreateNewThreadInteraction } from "features/Thread/hooks/useCanCreateNewThreadInteraction";
import { motion } from "framer-motion";
import {
  getAutocompleteQuerySuggestions,
  Paper,
  SearchMode,
} from "helpers/api";
import { getAutocompleteEndpointParams } from "helpers/endpointQueryParams";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import useSearchMode from "hooks/useSearchMode";
import { throttle } from "lodash";
import { useRouter } from "next/router";
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Autosuggest from "react-autosuggest";
import TextArea, { TextareaProps } from "react-expanding-textarea";
import { toast } from "react-toastify";
import { PremiumFeatureSecondaryButton } from "store/slices/subscription";
import useNewThread from "../hooks/useNewThread";
import { useNewThreadEvents } from "../hooks/useNewThreadEvents";
import { DataSourceSelector } from "features/DataSourceSelector";
import { useFeatureGate } from "features/Statsig";
import { FeatureFlag } from "features/Statsig/constants";
import AttachedThreadInteractionSources from "features/AskFollowUpQuestionInput/components/ThreadInteractionSources";

type NewThreadInputProps = {
  onClose?: () => void;
  sources?: Paper[];
  disallowRemovingSources?: boolean;
};

const WAIT_BETWEEN_AUTOSUGGEST_REQUESTS_MSEC = 1000;
const ACTION_SOURCE = MixpanelActionSource.NewThreadSearchBar;

/**
 * @component NewThreadInput
 * @description The text input that renders in the center of the New Thread Modal after selecting "new thread" from the sidebar.
 */
function NewThreadInput({
  onClose,
  sources,
  disallowRemovingSources = false,
}: NewThreadInputProps) {
  const router = useRouter();
  const [labels] = useLabels("features.new-thread-button");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
  const { currentSearchMode, changeSearchMode } = useSearchMode();

  const { createNewThread } = useNewThread();
  const { trackSearch } = useNewThreadEvents();
  const { redirectIfOutOfCredits } = useCheckSearchCredits();
  const checkCanCreate = useCanCreateNewThreadInteraction();

  const [newMessage, setNewMessage] = useState("");
  const textareaRef = useRef<HTMLInputElement | null>(null);

  const isDataSourceSelectorEnabled = useFeatureGate(
    FeatureFlag.DATA_SOURCE_SELECTOR
  ).value;

  // Automatically set search mode to PRO_ANALYSIS when sources are attached
  useEffect(() => {
    if (sources && sources.length > 0) {
      changeSearchMode(SearchMode.PRO_ANALYSIS);
    }
  }, [sources, changeSearchMode]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleMessageInputChange = (e: FormEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement;
    setNewMessage(target.value);
  };

  const handleSubmit = async () => {
    const [canCreate, message] = checkCanCreate(currentSearchMode);

    if (!canCreate) {
      if (message) {
        toast.error(message);
      }
      return;
    }

    onSearch(newMessage);
  };

  const onSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) return;
    const isOutOfCredits = redirectIfOutOfCredits(
      currentSearchMode,
      ACTION_SOURCE,
      {
        button: PremiumFeatureSecondaryButton.CREATE_NEW_THREAD,
        userMessage: searchQuery.trim(),
      }
    );
    if (isOutOfCredits) return;

    trackSearch(searchQuery.trim(), ACTION_SOURCE);
    createNewThread({
      userMessage: searchQuery.trim(),
      searchMode: currentSearchMode,
      actionSource: ACTION_SOURCE,
      sources: sources,
    });
    setNewMessage("");
    onClose?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length && event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedItemIndex((prevIndex) => (prevIndex + 1) % suggestions.length);
    } else if (suggestions.length && event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedItemIndex(
        (prevIndex) => (prevIndex - 1 + suggestions.length) % suggestions.length
      );
    } else {
      setSelectedItemIndex(-1);
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (selectedItemIndex === -1) {
        // Enter pressed on input field
        handleSubmit();
        return false;
      }
    }
  };

  const onSuggestionSelected = useCallback(
    (_: any, { suggestion }: { suggestion: string }) => {
      setNewMessage(suggestion);
      onSearch(suggestion);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSearchMode]
  );

  const onSuggestionsRequested = useMemo(() => {
    const loadSuggestions = async ({
      value,
    }: {
      value: string;
    }): Promise<void> => {
      let suggestions: string[] = [];
      try {
        const params = getAutocompleteEndpointParams(router.query);
        const result = await getAutocompleteQuerySuggestions(value);
        suggestions = result.queries;
      } catch (error) {
        // do nothing
      }
      setSuggestions(suggestions);
    };
    return throttle(loadSuggestions, WAIT_BETWEEN_AUTOSUGGEST_REQUESTS_MSEC);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSuggestionsCleared = useCallback(() => {
    setSuggestions([]);
  }, []);

  const enterKeyHintStaticallyTypedToPassTypeCheck: "go" = "go";
  const inputProps = {
    value: newMessage,
    placeholder:
      sources && sources.length > 0
        ? labels["ask-this-paper"]
        : labels["ask-the-research"],
    onChange: handleMessageInputChange,
    ref: textareaRef,
    type: "text",
    enterKeyHint: enterKeyHintStaticallyTypedToPassTypeCheck,
    "data-testid": "new-thread-input",
    className:
      "grow w-full overflow-hidden outline-hidden resize-none text-fg-base",
    onKeyDown: handleKeyDown,
    name: "newMessage",
  };

  const renderSearchInput = (inputProps: TextareaProps) => {
    return (
      <motion.div
        id="search-input-box"
        className={classNames(
          "flex flex-col pr-3 pb-2 pl-4 space-y-2 w-full h-full rounded-2xl bg-bg-base max-w-page",
          !!sources && sources.length > 0 ? "pt-2" : "pt-4"
        )}
        initial={{ outline: "0px solid var(--cdl-border-base)" }}
        animate={{
          outline: "2px solid var(--cdl-border-base)",
          outlineOffset: "2px",
        }}
        exit={{
          outline: "0px solid var(--cdl-border-base)",
          outlineOffset: "0px",
        }}
        transition={{ duration: 0.15, delay: 0.15 }}
      >
        <AttachedThreadInteractionSources
          className="-ml-2 mb-3"
          disableRemove={disallowRemovingSources}
          sourcesOverride={sources}
        />
        <TextArea {...inputProps} />
        <div className="flex">
          <div className="flex items-center space-x-2 grow">
            <div className="flex items-center -ml-3">
              {isDataSourceSelectorEnabled && <DataSourceSelector />}
              <SearchModeSelector
                hideLabelForMobile={isDataSourceSelectorEnabled}
                className={isDataSourceSelectorEnabled ? "-ml-1" : undefined}
              />
            </div>
          </div>

          <div className="flex gap-2 items-center">
            <FilterButton hideLabelForMobile={isDataSourceSelectorEnabled} />
            <CoButton
              disabled={newMessage.length === 0}
              onClick={handleSubmit}
              icon="icon-arrow-right"
              size="sm"
            />
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <form className="relative mx-auto leading">
      <Autosuggest
        suggestions={inputProps.value ? suggestions : []}
        onSuggestionsFetchRequested={onSuggestionsRequested}
        onSuggestionsClearRequested={onSuggestionsCleared}
        getSuggestionValue={(suggestion: string) => suggestion}
        renderSuggestion={(suggestion: string, { isHighlighted }) => (
          <span
            className={classNames("flex rounded-xl", {
              "bg-bg-faint": isHighlighted,
            })}
          >
            <span className="w-3"></span>
            <span
              className="flex flex-1 justify-between items-center py-3 border-b border-b-border-base"
              id="suggestion-text"
            >
              <span>{suggestion}</span>
              <span className="w-5 h-6">
                {isHighlighted ? (
                  <i className="text-xl icon-arrow-right text-fg-muted" />
                ) : null}
              </span>
            </span>
            <span className="w-3"></span>
          </span>
        )}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={(value, reason) => true}
        inputProps={inputProps}
        renderInputComponent={renderSearchInput}
        renderSuggestionsContainer={({ containerProps, children }) => (
          <div
            {...containerProps}
            key={containerProps.key}
            className={`${
              containerProps.className
            } ${"border-t border-t-border-base"}`}
          >
            {children}
          </div>
        )}
      />
    </form>
  );
}

export default NewThreadInput;
